import React, { useState, useEffect } from 'react';
import { Journey, JourneyStep } from '../../types/journey';
import { PersonaBadge } from './PersonaBadge';
import { useJourneyPoints } from '../../hooks/useJourneyPoints';
import { usePoints } from '../../context/PointsContext';
import { useLanguage } from '../../context/LanguageContext';
import './JourneySummary.css';

interface JourneySummaryProps {
  journey: Journey;
  steps: JourneyStep[];
  completedSteps: number;
  totalSteps: number;
  onReset: () => void;
}

export const JourneySummary: React.FC<JourneySummaryProps> = ({
  journey,
  steps,
  completedSteps,
  totalSteps,
  onReset
}) => {
  const { t } = useLanguage();
  const [pointsSummary, setPointsSummary] = useState<any>(null);
  const [milestones, setMilestones] = useState<any>(null);
  
  const { userPoints } = usePoints();
  const { getJourneyPointsSummary } = useJourneyPoints();
  const getMarketIcon = (market: string) => {
    switch (market) {
      case 'IT/Tech':
        return '💻';
      case 'E-commerce':
        return '🛒';
      case 'Social Media/Influencer':
        return '📱';
      case 'Other':
        return '🏢';
      default:
        return '🚀';
    }
  };

  const getStageIcon = (stage: string) => {
    switch (stage) {
      case 'Beginner':
        return '🌱';
      case 'Have Idea':
        return '💡';
      case 'In Progress':
        return '🚀';
      default:
        return '📈';
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  const progressPercentage = (completedSteps / totalSteps) * 100;

  // Load points summary
  useEffect(() => {
    const loadPointsSummary = async () => {
      try {
        const result = await getJourneyPointsSummary(journey.id);
        setPointsSummary(result.summary);
        setMilestones(result.milestones);
      } catch (error) {
        console.error('Error loading points summary:', error);
      }
    };

    loadPointsSummary();
  }, [journey.id, getJourneyPointsSummary, completedSteps]);

  const getMilestoneStatus = () => {
    if (!milestones) return null;
    
    if (milestones.completeMilestone) {
      return { text: t('journey.journeyComplete'), color: '#10b981' };
    } else if (milestones.halfMilestone) {
      return { text: t('journey.halfwayThere'), color: '#3b82f6' };
    } else if (milestones.quarterMilestone) {
      return { text: t('journey.greatStart'), color: '#f59e0b' };
    }
    return null;
  };

  return (
    <div className="journey-summary">
      <div className="summary-header">
        <h3>{t('journey.journeyOverview')}</h3>
      </div>

      <div className="summary-content">
        <div className="summary-section">
          <div className="summary-item">
            <span className="summary-label">{t('journey.persona')}</span>
            <PersonaBadge persona={journey.persona} size="small" />
          </div>

          <div className="summary-item">
            <span className="summary-label">{t('journey.stage')}</span>
            <div className="summary-value">
              <span className="summary-icon">{getStageIcon(journey.stage)}</span>
              <span>{t(`journey.stage${journey.stage.replace(/\s+/g, '')}`)}</span>
            </div>
          </div>

          <div className="summary-item">
            <span className="summary-label">{t('journey.market')}</span>
            <div className="summary-value">
              <span className="summary-icon">{getMarketIcon(journey.market)}</span>
              <span>{t(`journey.market${journey.market.replace(/[\/\s]/g, '')}`)}</span>
            </div>
          </div>

          <div className="summary-item">
            <span className="summary-label">{t('journey.started')}</span>
            <div className="summary-value">
              <span className="summary-icon">📅</span>
              <span>{formatDate(journey.createdAt)}</span>
            </div>
          </div>
        </div>

        <div className="summary-section">
          <div className="progress-summary">
            <div className="progress-header">
              <span className="progress-title">{t('journey.progress')}</span>
              <span className="progress-stats">
                {completedSteps}/{totalSteps}
              </span>
            </div>
            
            <div className="progress-visual">
              <div className="progress-track">
                <div 
                  className="progress-fill"
                  style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                />
              </div>
              <span className="progress-percentage">
                {Math.round(progressPercentage)}%
              </span>
            </div>

            {getMilestoneStatus() && (
              <div 
                className="milestone-badge"
                style={{ color: getMilestoneStatus()!.color }}
              >
                {getMilestoneStatus()!.text}
              </div>
            )}
          </div>
        </div>

        {journey.evaluation && (
          <div className="summary-section">
            <h4>{t('journey.yourEvaluation')}</h4>
            <div className="evaluation-grid">
              <div className="eval-item">
                <span className="eval-label">{t('journey.evaluationLabelIdea')}</span>
                <div className="eval-rating">
                  {Array.from({ length: 4 }, (_, i) => (
                    <span 
                      key={i} 
                      className={`eval-dot ${i < journey.evaluation!.idea ? 'filled' : ''}`}
                    />
                  ))}
                </div>
              </div>
              
              <div className="eval-item">
                <span className="eval-label">{t('journey.evaluationLabelSkills')}</span>
                <div className="eval-rating">
                  {Array.from({ length: 4 }, (_, i) => (
                    <span 
                      key={i} 
                      className={`eval-dot ${i < journey.evaluation!.skills ? 'filled' : ''}`}
                    />
                  ))}
                </div>
              </div>
              
              <div className="eval-item">
                <span className="eval-label">{t('journey.evaluationLabelNetwork')}</span>
                <div className="eval-rating">
                  {Array.from({ length: 4 }, (_, i) => (
                    <span 
                      key={i} 
                      className={`eval-dot ${i < journey.evaluation!.network ? 'filled' : ''}`}
                    />
                  ))}
                </div>
              </div>
              
              <div className="eval-item">
                <span className="eval-label">{t('journey.evaluationLabelTime')}</span>
                <div className="eval-rating">
                  {Array.from({ length: 4 }, (_, i) => (
                    <span 
                      key={i} 
                      className={`eval-dot ${i < journey.evaluation!.time ? 'filled' : ''}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="summary-actions">
          <button 
            className="reset-button"
            onClick={onReset}
          >
            <span className="reset-icon">🔄</span>
            {t('journey.resetJourney')}
          </button>
        </div>
      </div>
    </div>
  );
};